import {
  RhUiSeverityCriticalFillIcon,
  RhUiSeverityImportantFillIcon,
  RhUiSeverityModerateFillIcon,
  RhUiSeverityNoneFillIcon
} from "@patternfly/react-icons";
// Type-only imports, so pinning the config to PatternFly's own prop types costs nothing at runtime.
import type { LabelProps } from "@patternfly/react-core";
import type { SVGIconProps } from "@patternfly/react-icons/dist/esm/createIcon";
import type { ComponentType } from "react";
import { BaseEntity } from "./BaseEntity";

export interface Flag extends BaseEntity {
  targetEntity: FlagTargetEntity;
  targetId: number;
  triggeredAt: Date;
  flagId: number;
  flagName: string;
  flagLevel: FlagLevel;
}

export interface FlagDefinition extends BaseEntity {
  name: string;
  description?: string;
  level: FlagLevel;
  targetEntity: FlagTargetEntity;
  definition: string;
  enabled: boolean;
}

export enum FlagLevel {
  ALERT = 'ALERT',
  IMPORTANT = 'IMPORTANT',
  CRITICAL = 'CRITICAL',
  INFO = 'INFO'
}

/** The levels most-severe first, which is the order every list and legend shows them in. */
export const FLAG_LEVELS_BY_SEVERITY: FlagLevel[] = [
  FlagLevel.CRITICAL,
  FlagLevel.IMPORTANT,
  FlagLevel.ALERT,
  FlagLevel.INFO
];

/**
 * The one place a flag level's icon, colour and wording are decided. Every screen that paints a
 * flag reads it from here, so a level looks the same in the calendar, the table and the legend.
 * `icon` is the component itself, not an element, so callers can size and colour it themselves.
 */
export const getFlagLevelConfig = (level: FlagLevel) => {
  const map: Record<FlagLevel, { color: LabelProps['color']; text: string; icon: ComponentType<SVGIconProps>; iconColor: string }> = {
    [FlagLevel.CRITICAL]: {
      color: 'red',
      text: 'Critical',
      icon: RhUiSeverityCriticalFillIcon,
      iconColor: 'var(--pf-t--global--icon--color--severity--critical--default, #b1380b)'
    },
    [FlagLevel.IMPORTANT]: {
      color: 'orange',
      text: 'Important',
      icon: RhUiSeverityImportantFillIcon,
      iconColor: 'var(--pf-t--global--icon--color--severity--important--default, #9e4a06)'
    },
    [FlagLevel.ALERT]: {
      color: 'yellow',
      text: 'Alert',
      icon: RhUiSeverityModerateFillIcon,
      iconColor: 'var(--pf-t--global--icon--color--severity--moderate--default, #73480b)'
    },
    [FlagLevel.INFO]: {
      color: 'blue',
      text: 'Info',
      icon: RhUiSeverityNoneFillIcon,
      iconColor: 'var(--pf-t--global--icon--color--severity--none--default, #004d99)'
    }
  };
  return map[level];
};

export enum FlagTargetEntity {
  KLASS = 'KLASS',
  INSTRUCTOR = 'INSTRUCTOR'
}
